import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { AeonyxWordmark } from "@/components/aeonyx/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LockKey } from "@phosphor-icons/react";

export default function ResetPassword() {
  const { updatePassword } = useAuth();
  const [recovery, setRecovery] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
    });
    // Also treat as recovery if user landed here with an active session
    (async () => {
      const { data: s } = await supabase.auth.getSession();
      if (s.session) setRecovery(true);
    })();
    return () => data.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      toast.success("Password updated. Please sign in.");
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.message || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cool-white flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Link to="/" aria-label="Home">
            <AeonyxWordmark className="h-12 w-auto" />
          </Link>
        </div>
        <div className="rounded-3xl border border-[#8A97A8]/25 bg-white p-8 shadow-[0_30px_60px_-20px_rgba(2,47,107,0.2)]">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-navy-deep tracking-tight text-center">
            Set a new password
          </h2>
          <p className="mt-1.5 text-sm text-cool-grey text-center">
            {recovery
              ? "Choose a strong password of at least 6 characters."
              : "Waiting for a valid reset link…"}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div>
              <Label htmlFor="pw" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
                New password
              </Label>
              <div className="mt-1.5 relative">
                <LockKey
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-cool-grey"
                />
                <Input
                  id="pw"
                  data-testid="reset-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-11 pl-9 rounded-xl bg-white border-[#8A97A8]/30"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="pw2" className="text-[11px] uppercase tracking-[0.12em] text-navy-deep">
                Confirm password
              </Label>
              <Input
                id="pw2"
                data-testid="reset-password-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className="mt-1.5 h-11 rounded-xl bg-white border-[#8A97A8]/30"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !recovery}
              data-testid="reset-submit"
              className="w-full h-11 rounded-full bg-cta-gradient text-white font-semibold shadow-[0_8px_22px_-8px_rgba(1,207,198,0.55)]"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
